import React from 'react';
import { Shield, Users, Database, Activity, Server, Radio, Key } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export function SystemPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  if (!isSuperAdmin) {
    navigate('/');
    return null;
  }

  const stats = [
    {
      label: 'Total Organizations',
      value: '42',
      icon: Users,
      color: 'text-accent-blue',
    },
    {
      label: 'Total Users',
      value: '327',
      icon: Users,
      color: 'text-accent-green',
    },
    {
      label: 'Edge Devices',
      value: '8',
      icon: Server,
      color: 'text-accent-purple',
    },
    {
      label: 'Total Executions',
      value: '12,459',
      icon: Activity,
      color: 'text-accent-orange',
    },
  ];

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            System Administration
          </h1>
          <p className="text-body text-muted-foreground">
            Platform-wide configuration and monitoring
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map(stat => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg bg-background-subtle flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* System Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Database Status
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="text-sm font-medium text-status-success">Healthy</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Documents</span>
                  <span className="text-sm font-medium text-foreground">1,247,893</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Storage Used</span>
                  <span className="text-sm font-medium text-foreground">42.7 GB</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Read/Write Operations</span>
                  <span className="text-sm font-medium text-foreground">124K / 89K</span>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                System Health
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">API Response Time</span>
                  <span className="text-sm font-medium text-foreground">127ms</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                  <span className="text-sm font-medium text-status-success">99.97%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Active Connections</span>
                  <span className="text-sm font-medium text-foreground">342</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Uptime</span>
                  <span className="text-sm font-medium text-foreground">99.99%</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mt-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/admin/users')}
                className="p-4 rounded-lg border border-border hover:border-accent-blue
                         transition-colors text-left group"
              >
                <Users className="w-5 h-5 text-accent-blue mb-2" />
                <p className="font-medium text-foreground group-hover:text-accent-blue">
                  Manage Users
                </p>
                <p className="text-sm text-muted-foreground">
                  Create and manage users across all organizations
                </p>
              </button>

                      <button
                        onClick={() => navigate('/admin/organizations')}
                        className="p-4 rounded-lg border border-border hover:border-accent-blue
                                 transition-colors text-left group"
                      >
                        <Server className="w-5 h-5 text-accent-blue mb-2" />
                        <p className="font-medium text-foreground group-hover:text-accent-blue">
                          Manage Organizations
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Create, edit, or delete organizations
                        </p>
                      </button>

              <button
                        onClick={() => navigate('/admin/modem-models')}
                        className="p-4 rounded-lg border border-border hover:border-accent-blue
                                 transition-colors text-left group"
                      >
                        <Radio className="w-5 h-5 text-accent-blue mb-2" />
                        <p className="font-medium text-foreground group-hover:text-accent-blue">
                          Modem Models Database
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Manage modem capabilities for intelligent scheduling
                        </p>
                      </button>

              <button
                className="p-4 rounded-lg border border-border hover:border-accent-blue
                         transition-colors text-left group"
              >
                <Server className="w-5 h-5 text-accent-blue mb-2" />
                <p className="font-medium text-foreground group-hover:text-accent-blue">
                  Edge Device Management
                </p>
                <p className="text-sm text-muted-foreground">
                  Configure edge devices and modems
                </p>
              </button>

              <button
                onClick={() => navigate('/admin/settings')}
                className="p-4 rounded-lg border border-border hover:border-accent-blue
                         transition-colors text-left group"
              >
                <Database className="w-5 h-5 text-accent-blue mb-2" />
                <p className="font-medium text-foreground group-hover:text-accent-blue">
                  System Configuration
                </p>
                <p className="text-sm text-muted-foreground">
                  Update system-wide settings
                </p>
              </button>

              <button
                onClick={() => navigate('/admin/system-api-keys')}
                className="p-4 rounded-lg border border-border hover:border-accent-blue
                         transition-colors text-left group"
              >
                <Key className="w-5 h-5 text-accent-blue mb-2" />
                <p className="font-medium text-foreground group-hover:text-accent-blue">
                  System API Keys
                </p>
                <p className="text-sm text-muted-foreground">
                  Manage platform-wide API keys for internal tools
                </p>
              </button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

